import {
  BaseVisualizer,
  CreateLink,
  MessageVisualizer,
  PREDEFINED_REGEX,
  RCSDashboardStatisticsMenu,
  getPredefinedRegexById,
  isPredefinedRegexType
} from "./chunk.SX64DSWQ.js";
import {
  COLUMNS,
  useActivateAgent,
  useCreateRcsSettings,
  useDeleteRcsSettings,
  useGetAgentList,
  useGetCTAEngagement,
  useGetRcsSettingsList,
  useGetStatsEngagement,
  useGetStatsTableEngagement,
  useGetStatsUnsubscription,
  useSuspenseGetRcsSettings,
  useUpdateRcsSettings
} from "./chunk.BNCPJJY3.js";
import "./chunk.LHTKCKFZ.js";
import {
  getPaginatedCampaigns
} from "./chunk.DJW5UPIU.js";

// src/lib/core/navigations.ts
import { faMobileScreen, faTable, faUserSecret } from "@oppy/omniv2-ui";
var navigations = [
  {
    id: "rcs-root",
    href: "/rcs",
    label: "RCS",
    requiredPermission: {
      resource: "RCSSetting",
      permission: "access"
    },
    subItems: [
      {
        id: "rcs-list",
        href: "/rcs",
        label: "Liste des RCS",
        iconProps: {
          icon: faMobileScreen
        },
        requiredPermission: {
          resource: "RCSSetting",
          permission: "read"
        }
      },
      {
        id: "rcs-list-agent",
        href: "/rcs/agent",
        label: "Agents RCS",
        iconProps: {
          icon: faUserSecret
        },
        requiredPermission: {
          resource: "RCSAgent",
          permission: "read"
        }
      },
      {
        id: "rcs-campaigns",
        href: "/rcs/campaigns",
        label: "Campagnes RCS",
        iconProps: {
          icon: faTable
        },
        requiredPermission: {
          resource: "RCSCampaign",
          permission: "access"
        }
      }
    ]
  }
];
var navigations_default = navigations;

// src/lib/core/routes.tsx
import { RouteGuard } from "@oppy/omniv2-core";
import { PERMISSIONS as PERMISSIONS3, RESOURCES as RESOURCES5 } from "@oppy/omniv2-utils";

// src/pages/campaigns/rcs-campaigns-page.tsx
import { useCurrentUser } from "@oppy/omniv2-core";
import { Card, Loading, Table, ToggleButton, Tooltip } from "@oppy/omniv2-ui";
import { usePermissions, USER_ROLES } from "@oppy/omniv2-utils";
import { Suspense, useState } from "react";
import { Navigate } from "react-router-dom";

// src/pages/campaigns/constants.ts
var RCS_TABLE_STATUS_MODES = {
  IN_PROGRESS: "IN_PROGRESS",
  TERMINATED: "TERMINATED"
};
var TOOLTIP_ATTR = "data-campaign-id";
var TOOLTIP_CLASS = "tooltip-anchor";

// src/pages/campaigns/hooks/use-campaign-columns.tsx
import { useToastContext } from "@oppy/omniv2-ui";
import {
  AppLink,
  faCircleCheck,
  faCopy,
  faEye,
  Icon,
  IconButton
} from "@oppy/omniv2-ui";
import { useClipboard } from "@oppy/omniv2-utils";

// src/pages/campaigns/components/percentage-cell.tsx
import { percentageWithPrecision } from "@oppy/omniv2-utils";
import { jsx, jsxs } from "react/jsx-runtime";
function PercentageCell({ value, total }) {
  return /* @__PURE__ */ jsxs("div", { className: "flex flex-col items-center text-sm", children: [
    /* @__PURE__ */ jsx("span", { className: "font-medium", children: value }),
    /* @__PURE__ */ jsxs("span", { className: "text-xs text-gray-500", children: [
      percentageWithPrecision(total)(value),
      " %"
    ] })
  ] });
}

// src/pages/campaigns/hooks/use-campaign-columns.tsx
import { jsx as jsx2, jsxs as jsxs2 } from "react/jsx-runtime";
var formatDate = (date) => {
  if (!date) return "";
  return new Date(date).toLocaleDateString("fr-FR");
};
var handleCopyId = (copyFn, value, showMessage) => async (e) => {
  e.stopPropagation();
  try {
    await copyFn(value);
    showMessage({
      severity: "success",
      content: () => /* @__PURE__ */ jsxs2("div", { className: "flex w-full items-center justify-between gap-4", children: [
        /* @__PURE__ */ jsx2(Icon, { icon: faCircleCheck, className: "w-auto" }),
        /* @__PURE__ */ jsx2("span", { className: "w-full", children: "Copi\xE9 dans le presse-papier" })
      ] }),
      closable: false
    });
  } catch {
    showMessage({
      severity: "error",
      content: "nope",
      closable: false
    });
  }
};
var createColumn = ({
  field,
  header,
  className,
  body
}) => ({
  field,
  header,
  className,
  body
});
function useCampaignColumns() {
  const { copyToClipboard } = useClipboard();
  const { showMessage } = useToastContext();
  const makeColumns = () => [
    createColumn({
      field: "name",
      header: "Nom",
      className: "w-full",
      body: ({ name, id }) => /* @__PURE__ */ jsxs2("div", { className: "flex w-full items-center justify-between whitespace-normal break-all", children: [
        /* @__PURE__ */ jsx2("span", { className: "min-w-44", children: name }),
        /* @__PURE__ */ jsx2(
          Icon,
          {
            className: `${TOOLTIP_CLASS} size-4 cursor-pointer`,
            icon: faCopy,
            onClick: handleCopyId(copyToClipboard, id, showMessage),
            ...{ [TOOLTIP_ATTR]: name }
          }
        )
      ] })
    }),
    createColumn({
      field: "creationDate",
      header: "Date de cr\xE9ation",
      className: "w-full md:w-1/6",
      body: ({ creationDate }) => formatDate(creationDate)
    }),
    createColumn({
      field: "startDate",
      header: "Date de d\xE9but",
      className: "w-full md:w-1/6",
      body: ({ startDate }) => formatDate(startDate)
    }),
    createColumn({
      field: "totalContacts",
      header: "Confi\xE9s",
      body: ({ totalContacts }) => /* @__PURE__ */ jsx2("div", { className: "flex flex-col items-center", children: totalContacts })
    }),
    createColumn({
      field: "numberOfContactsOk",
      header: "Valides",
      body: ({ totalContacts, numberOfContactsOk }) => /* @__PURE__ */ jsx2(PercentageCell, { value: numberOfContactsOk, total: totalContacts })
    }),
    createColumn({
      field: "numberOfContactsInvalid",
      header: "Invalides",
      body: ({ totalContacts, numberOfContactsInvalid }) => /* @__PURE__ */ jsx2(PercentageCell, { value: numberOfContactsInvalid, total: totalContacts })
    }),
    createColumn({
      field: "numberOfContactsDuplicated",
      header: "Doublons",
      body: ({ totalContacts, numberOfContactsDuplicated }) => /* @__PURE__ */ jsx2(
        PercentageCell,
        {
          value: numberOfContactsDuplicated,
          total: totalContacts
        }
      )
    }),
    createColumn({
      field: "numberOfContactsBlacklisted",
      header: "Blacklist\xE9s",
      body: ({
        totalContacts,
        numberOfContactsBlacklisted,
        numberOfContactsDeny
      }) => /* @__PURE__ */ jsx2(
        PercentageCell,
        {
          value: numberOfContactsBlacklisted + numberOfContactsDeny,
          total: totalContacts
        }
      )
    }),
    createColumn({
      field: "waitingContact",
      header: "En attente",
      body: ({ totalContacts, waitingContact }) => /* @__PURE__ */ jsx2(PercentageCell, { value: waitingContact, total: totalContacts })
    }),
    createColumn({
      field: "runningContact",
      header: "En cours",
      body: ({ totalContacts, runningContact }) => /* @__PURE__ */ jsx2(PercentageCell, { value: runningContact, total: totalContacts })
    }),
    createColumn({
      field: "finishedContact",
      header: "Termin\xE9s",
      body: ({ totalContacts, finishedContact }) => /* @__PURE__ */ jsx2(PercentageCell, { value: finishedContact, total: totalContacts })
    }),
    createColumn({
      field: "id",
      header: "",
      body: ({ id }) => /* @__PURE__ */ jsx2("div", { className: "relative block px-8", children: /* @__PURE__ */ jsx2(AppLink, { to: `/rcs/campaigns/${id}/dashboard`, children: /* @__PURE__ */ jsx2(IconButton, { iconProps: { icon: faEye }, size: "extra-small", circle: true }) }) })
    })
  ];
  return { columns: makeColumns() };
}

// src/pages/campaigns/hooks/use-rcs-campaigns.tsx
import {
  CampaignStatus
} from "@oppy/omniv2-core";
import { useEntity } from "@oppy/omniv2-entity";
import { useTablePagination } from "@oppy/omniv2-ui";

// src/lib/hooks/campaigns/use-fetch-paginated-campaigns.tsx
import { useQuery } from "@tanstack/react-query";
function useFetchPaginatedCampaigns({
  entityId,
  payload
}) {
  const queryKey = [
    "rcs-campaigns",
    payload.campaignSettingsId,
    payload.page,
    payload.take,
    payload.descending,
    JSON.stringify(payload.statuses)
  ];
  return useQuery({
    queryKey,
    queryFn: () => getPaginatedCampaigns({
      entityId,
      payload
    }),
    refetchOnWindowFocus: true
  });
}

// src/pages/campaigns/hooks/use-rcs-campaigns.tsx
var statusesToFetchMap = {
  [RCS_TABLE_STATUS_MODES.IN_PROGRESS]: [
    CampaignStatus.RUNNING,
    CampaignStatus.WAITING,
    CampaignStatus.PAUSED
  ],
  [RCS_TABLE_STATUS_MODES.TERMINATED]: [
    CampaignStatus.COMPLETED,
    CampaignStatus.CANCELLED
  ]
};
function useRcsCampaigns({
  viewMode
}) {
  const { searchParams, ...tablePaginationProps } = useTablePagination();
  const { entityId } = useEntity();
  const statuses = statusesToFetchMap[viewMode];
  const payload = {
    take: parseInt(
      searchParams.get("pageSize") || tablePaginationProps.params.rows.toString(),
      10
    ),
    page: parseInt(
      searchParams.get("pageNumber") || (tablePaginationProps.params.first + 1).toString(),
      10
    ),
    statuses,
    campaignSettingsId: searchParams.get("campaignSettingsId") || "",
    descending: searchParams.get("sortDirection") === "desc" || true
  };
  const { isLoading, data = {} } = useFetchPaginatedCampaigns({
    entityId,
    payload
  });
  return {
    isLoading,
    data,
    searchParams,
    ...tablePaginationProps
  };
}

// src/pages/campaigns/rcs-campaigns-page.tsx
import { jsx as jsx3, jsxs as jsxs3 } from "react/jsx-runtime";
var viewModeOptions = [
  { value: RCS_TABLE_STATUS_MODES.IN_PROGRESS, label: "En cours" },
  { value: RCS_TABLE_STATUS_MODES.TERMINATED, label: "Termin\xE9es" }
];
function RcsCampaignsPage() {
  const currentUser = useCurrentUser();
  const { userRole } = usePermissions(currentUser ?? void 0);
  const [viewMode, setViewMode] = useState(
    RCS_TABLE_STATUS_MODES.IN_PROGRESS
  );
  const {
    data: { items, totalItems },
    isLoading,
    onPage,
    onSort,
    params: { first, rows, sortField, sortOrder }
  } = useRcsCampaigns({ viewMode });
  const { columns } = useCampaignColumns();
  const [tooltipText, setToolptipText] = useState("");
  if (userRole !== USER_ROLES.OPPY) {
    return /* @__PURE__ */ jsx3(Navigate, { to: "/forbidden", replace: true });
  }
  return /* @__PURE__ */ jsxs3(Card, { children: [
    /* @__PURE__ */ jsx3("div", { className: "mb-4 flex justify-start", children: /* @__PURE__ */ jsx3("div", { className: "grid w-fit grid-flow-col gap-3", children: viewModeOptions.map((option) => /* @__PURE__ */ jsx3(
      ToggleButton,
      {
        selected: viewMode === option.value,
        onClick: () => setViewMode(option.value),
        children: option.label
      },
      option.value
    )) }) }),
    /* @__PURE__ */ jsxs3(Suspense, { fallback: /* @__PURE__ */ jsx3(Loading, {}), children: [
      /* @__PURE__ */ jsx3(
        Table,
        {
          columns,
          first,
          loading: isLoading,
          rows,
          sortField,
          sortOrder,
          showTotalRecords: true,
          totalRecords: totalItems,
          value: items ?? [],
          onPage,
          onSort,
          resizableColumns: true,
          tableClassName: "table-auto",
          tableStyle: { tableLayout: "fixed", width: "100%" }
        }
      ),
      /* @__PURE__ */ jsx3(
        Tooltip,
        {
          target: `.${TOOLTIP_CLASS}`,
          children: /* @__PURE__ */ jsx3("span", { children: tooltipText }),
          position: "right",
          at: "right+5 top",
          my: "left center-2",
          onBeforeShow: (e) => {
            const campaignId = e.target.getAttribute(TOOLTIP_ATTR) || "";
            setToolptipText(`Copie l'ID de la campagne ${campaignId}`);
          },
          onHide: () => setToolptipText("")
        }
      )
    ] })
  ] });
}

// src/lib/core/routes.tsx
import { Outlet } from "react-router-dom";

// src/pages/agent/consult/rcs-agent-consult.page.tsx
import { Loading as Loading2 } from "@oppy/omniv2-ui";
import { lazy, Suspense as Suspense2 } from "react";
import { jsx as jsx4 } from "react/jsx-runtime";
var RcsAgent = lazy(() => import("./consult-agent.RZENEM3S.js"));
function RcsAgentConsultPage() {
  return /* @__PURE__ */ jsx4(Suspense2, { fallback: /* @__PURE__ */ jsx4(Loading2, {}), children: /* @__PURE__ */ jsx4(RcsAgent, {}) });
}

// src/pages/agent/create/rcs-agent-create.page.tsx
import { Heading1, Loading as Loading3 } from "@oppy/omniv2-ui";
import { lazy as lazy2, Suspense as Suspense3 } from "react";
import { jsx as jsx5, jsxs as jsxs4 } from "react/jsx-runtime";
var AgentForm = lazy2(() => import("./agent-form.GBNC74GL.js"));
function RcsAgentCreatePage() {
  return /* @__PURE__ */ jsxs4("div", { className: "flex flex-col", children: [
    /* @__PURE__ */ jsx5("div", { className: "mb-5 flex items-center justify-between", children: /* @__PURE__ */ jsx5(Heading1, { children: "Creation Agent RCS" }) }),
    /* @__PURE__ */ jsx5(Suspense3, { fallback: /* @__PURE__ */ jsx5(Loading3, {}), children: /* @__PURE__ */ jsx5("div", { className: "flex gap-x-4", children: /* @__PURE__ */ jsx5(AgentForm, {}) }) })
  ] });
}

// src/pages/agent/list/rcs-agent-list.page.tsx
import { ComponentGuard } from "@oppy/omniv2-core";
import { Heading1 as Heading12, Table as Table2 } from "@oppy/omniv2-ui";
import { PERMISSIONS, RESOURCES as RESOURCES2 } from "@oppy/omniv2-utils";
import { Fragment } from "react/jsx-runtime";
import { useNavigate } from "react-router-dom";

// src/pages/agent/list/hooks/use-rcs-agent-list-page.ts
import { useEntity as useEntity2 } from "@oppy/omniv2-entity";
import { useTablePagination as useTablePagination2 } from "@oppy/omniv2-ui";
function useRcsAgentListPage() {
  const { entityId } = useEntity2();
  const { searchParams, ...tablePaginationProps } = useTablePagination2();
  const { isLoading, data = {} } = useGetAgentList({
    entityId,
    searchParams
  });
  return {
    isLoading,
    data,
    searchParams,
    ...tablePaginationProps
  };
}

// src/pages/agent/list/rcs-agent-list.constants.tsx
import { Badge } from "@oppy/omniv2-ui";
import { formatDate as formatDate2 } from "@oppy/omniv2-utils";

// src/pages/agent/list/components/rcs-agent-action-menu.tsx
import { useCurrentUser as useCurrentUser2 } from "@oppy/omniv2-core";
import { faCheck, faEye as faEye2 } from "@oppy/omniv2-ui";
import { buttonTemplate, linkTemplate, PopupMenu } from "@oppy/omniv2-ui";
import { RESOURCES, usePermissions as usePermissions2 } from "@oppy/omniv2-utils";

// src/pages/agent/list/hooks/use-rcs-agent-action-menu.ts
import { useEntity as useEntity3 } from "@oppy/omniv2-entity";
function useRcsAgentActionMenu({ agentId, status }) {
  const { entityId } = useEntity3();
  const activateAgent = useActivateAgent();
  const disabled = status === "Validated";
  const handleClickActivateAgent = () => {
    activateAgent({ agentId, entityId });
  };
  return {
    disabled,
    handleClickActivateAgent
  };
}

// src/pages/agent/list/components/rcs-agent-action-menu.tsx
import { jsx as jsx6 } from "react/jsx-runtime";
function RcsAgentActionMenu({ agentId, status }) {
  const currentUser = useCurrentUser2();
  const { canRead, canUpdate } = usePermissions2(currentUser ?? void 0);
  const { disabled, handleClickActivateAgent } = useRcsAgentActionMenu({
    agentId,
    status
  });
  const canReadRcsAgent = canRead(RESOURCES.RCS_AGENT);
  const canUpdateRcsAgent = canUpdate(RESOURCES.RCS_AGENT);
  if (!canReadRcsAgent && !canUpdateRcsAgent) {
    return null;
  }
  const menuItems = [];
  if (canReadRcsAgent) {
    menuItems.push(
      linkTemplate({ label: "Consulter l'agent", to: agentId, icon: faEye2 })
    );
  }
  if (canUpdateRcsAgent) {
    menuItems.push(
      buttonTemplate({
        label: "Activer l'agent",
        disabled,
        onClick: handleClickActivateAgent,
        icon: faCheck
      })
    );
  }
  return /* @__PURE__ */ jsx6(PopupMenu, { items: menuItems });
}

// src/pages/agent/list/rcs-agent-list.constants.tsx
import { jsx as jsx7 } from "react/jsx-runtime";
var COLUMNS2 = [
  { field: "name", header: "Nom", sortable: true },
  { field: "description", header: "Description", sortable: true },
  {
    field: "status",
    header: "Statut",
    body: ({ status }) => {
      const isActive = status === "Validated";
      const variant = isActive ? "success" : "warning";
      const isActiveLabel = isActive ? "Actif" : "En validation";
      return /* @__PURE__ */ jsx7(Badge, { variant, children: isActiveLabel });
    },
    sortable: false
  },
  {
    field: "updatedAt",
    header: "Modification",
    body: ({ updatedAt }) => formatDate2(updatedAt),
    sortable: true
  },
  {
    field: "actions",
    header: "Actions",
    body: ({ id, status }) => /* @__PURE__ */ jsx7(RcsAgentActionMenu, { agentId: id, status })
  }
];

// src/pages/agent/list/rcs-agent-list.page.tsx
import { jsx as jsx8, jsxs as jsxs5 } from "react/jsx-runtime";
function RcsAgentListPage() {
  const {
    data: { items, totalItems },
    isLoading,
    params: { first, rows, sortField, sortOrder },
    onPage,
    onSort
  } = useRcsAgentListPage();
  const navigate = useNavigate();
  const handleAgentConsult = (id) => navigate(id);
  return /* @__PURE__ */ jsxs5(Fragment, { children: [
    /* @__PURE__ */ jsxs5("div", { className: "mb-5 flex items-center justify-between", children: [
      /* @__PURE__ */ jsx8(Heading12, { children: "Agents RCS" }),
      /* @__PURE__ */ jsx8(
        ComponentGuard,
        {
          resource: RESOURCES2.RCS_AGENT,
          permission: PERMISSIONS.CREATE,
          children: /* @__PURE__ */ jsx8(CreateLink, { to: "create", label: "Cr\xE9er un Agent" })
        }
      )
    ] }),
    /* @__PURE__ */ jsx8(
      Table2,
      {
        columns: COLUMNS2,
        first,
        loading: isLoading,
        onRowClick: ({ data: { id } }) => handleAgentConsult(id),
        rows,
        showTotalRecords: true,
        sortField,
        sortOrder,
        totalRecords: totalItems,
        value: items ?? [],
        onPage,
        onSort,
        rowClassName: () => "hover:bg-gray-100 hover:cursor-pointer transition-colors"
      }
    )
  ] });
}

// src/pages/rcs/create/rcs-create.page.tsx
import { Loading as Loading4 } from "@oppy/omniv2-ui";
import { lazy as lazy3, Suspense as Suspense4 } from "react";

// src/pages/rcs/create/hooks/use-rcs-create-page.ts
import { useEntity as useEntity4 } from "@oppy/omniv2-entity";
import { useNavigate as useNavigate2 } from "react-router-dom";
function useRcsCreatePage() {
  const { entityId } = useEntity4();
  const navigate = useNavigate2();
  const onSuccessCallback = () => navigate("..");
  const { createRcsSettings, isPending } = useCreateRcsSettings();
  const handleSubmit = (rcsSettings) => createRcsSettings({ entityId, rcsSettings, onSuccessCallback });
  return {
    isPending,
    handleSubmit
  };
}

// src/pages/rcs/create/rcs-create.page.tsx
import { jsx as jsx9 } from "react/jsx-runtime";
var RcsWizardForm = lazy3(
  () => import("./rcs-wizard-form.FEOVWAO4.js")
);
function RcsCreatePage() {
  const { isPending, handleSubmit } = useRcsCreatePage();
  return /* @__PURE__ */ jsx9(Suspense4, { fallback: /* @__PURE__ */ jsx9(Loading4, {}), children: /* @__PURE__ */ jsx9(
    RcsWizardForm,
    {
      title: "Cr\xE9ation du RCS",
      handleSubmit,
      isSubmitting: isPending
    }
  ) });
}

// src/pages/rcs/list/rcs-list.page.tsx
import { ComponentGuard as ComponentGuard2 } from "@oppy/omniv2-core";
import { Heading1 as Heading13, Table as Table3 } from "@oppy/omniv2-ui";
import { PERMISSIONS as PERMISSIONS2, RESOURCES as RESOURCES4 } from "@oppy/omniv2-utils";
import { useNavigate as useNavigate3 } from "react-router-dom";

// src/pages/rcs/list/hooks/use-rcs-list-page.ts
import { useEntity as useEntity5 } from "@oppy/omniv2-entity";
import { useTablePagination as useTablePagination3 } from "@oppy/omniv2-ui";
function useRcsListPage() {
  const { entityId } = useEntity5();
  const { searchParams, ...tablePaginationProps } = useTablePagination3();
  const { data = {}, isLoading } = useGetRcsSettingsList({
    entityId,
    searchParams
  });
  return {
    data,
    isLoading,
    searchParams,
    ...tablePaginationProps
  };
}

// src/pages/rcs/list/rcs-list.constants.tsx
import { Badge as Badge2 } from "@oppy/omniv2-ui";

// src/pages/rcs/list/components/rcs-settings-action-menu.tsx
import { useCurrentUser as useCurrentUser3 } from "@oppy/omniv2-core";
import { cn, confirmDialog, faTrash, Icon as Icon2 } from "@oppy/omniv2-ui";
import { RESOURCES as RESOURCES3, usePermissions as usePermissions3 } from "@oppy/omniv2-utils";

// src/pages/rcs/list/hooks/use-rcs-settings-action-menu.ts
import { useEntity as useEntity6 } from "@oppy/omniv2-entity";
function useRcsSettingsActionMenu({ rcsSettingsId }) {
  const deleteRcsSettings = useDeleteRcsSettings();
  const { entityId } = useEntity6();
  const handleConfirmDelete = () => {
    deleteRcsSettings({ entityId, rcsSettingsId });
  };
  return { handleConfirmDelete };
}

// src/pages/rcs/list/components/rcs-settings-action-menu.tsx
import { jsx as jsx10, jsxs as jsxs6 } from "react/jsx-runtime";
function RcsSettingsActionMenu({
  name,
  isUsed,
  rcsSettingsId
}) {
  const currentUser = useCurrentUser3();
  const { canDelete } = usePermissions3(currentUser ?? void 0);
  const { handleConfirmDelete } = useRcsSettingsActionMenu({ rcsSettingsId });
  const canDeleteRcsSetting = canDelete(RESOURCES3.RCS_SETTING);
  const handleClickDelete = () => confirmDialog({
    message: `Souhaitez-vous supprimer le RCS "${name}" ?`,
    defaultFocus: "reject",
    accept: handleConfirmDelete
  });
  if (!canDeleteRcsSetting) {
    return null;
  }
  return /* @__PURE__ */ jsxs6(
    "button",
    {
      type: "button",
      className: cn(
        "w-auto p-2 inline-flex justify-between items-center gap-2 text-error",
        { "cursor-not-allowed opacity-50": isUsed }
      ),
      onClick: (e) => {
        handleClickDelete();
        e?.stopPropagation();
      },
      disabled: isUsed,
      children: [
        "Supprimer",
        /* @__PURE__ */ jsx10(Icon2, { icon: faTrash, className: "pointer-events-none" })
      ]
    }
  );
}

// src/pages/rcs/list/rcs-list.constants.tsx
import { Fragment as Fragment2, jsx as jsx11 } from "react/jsx-runtime";
var COLUMNS3 = [
  {
    field: "name",
    header: "Nom",
    sortable: true,
    body: ({ name }) => /* @__PURE__ */ jsx11(Fragment2, { children: name })
  },
  { field: "description", header: "Description", sortable: true },
  {
    field: "isUsed",
    header: "Statut",
    body: ({ isUsed }) => {
      const variant = isUsed ? "success" : "disabled";
      const isActiveLabel = isUsed ? "Actif" : "Inactif";
      return /* @__PURE__ */ jsx11(Badge2, { variant, children: isActiveLabel });
    }
  },
  {
    header: "Actions",
    body: ({ id, isUsed, name }) => /* @__PURE__ */ jsx11(RcsSettingsActionMenu, { rcsSettingsId: id, isUsed, name })
  }
];

// src/pages/rcs/list/rcs-list.page.tsx
import { Fragment as Fragment3, jsx as jsx12, jsxs as jsxs7 } from "react/jsx-runtime";
function RcsListPage() {
  const {
    data: { items, totalItems },
    isLoading,
    params: { first, rows, sortField, sortOrder },
    onPage,
    onSort
  } = useRcsListPage();
  const navigate = useNavigate3();
  const handleRcsEdit = (id) => navigate(id);
  return /* @__PURE__ */ jsxs7(Fragment3, { children: [
    /* @__PURE__ */ jsxs7("div", { className: "mb-5 flex items-center justify-between", children: [
      /* @__PURE__ */ jsx12(Heading13, { children: "Liste des RCS" }),
      /* @__PURE__ */ jsx12(
        ComponentGuard2,
        {
          resource: RESOURCES4.RCS_SETTING,
          permission: PERMISSIONS2.CREATE,
          children: /* @__PURE__ */ jsx12(CreateLink, { to: "create", label: "Cr\xE9er un RCS" })
        }
      )
    ] }),
    /* @__PURE__ */ jsx12(
      Table3,
      {
        columns: COLUMNS3,
        first,
        loading: isLoading,
        onRowClick: ({ data: { id } }) => handleRcsEdit(id),
        rows,
        showTotalRecords: true,
        sortField,
        sortOrder,
        totalRecords: totalItems,
        value: items ?? [],
        onPage,
        onSort,
        rowClassName: () => "hover:bg-gray-100 hover:cursor-pointer transition-colors"
      }
    )
  ] });
}

// src/pages/rcs/update/rcs-update.page.tsx
import { Loading as Loading5 } from "@oppy/omniv2-ui";
import { lazy as lazy4, Suspense as Suspense5 } from "react";

// src/pages/rcs/update/hooks/use-rcs-update-page.ts
import { useEntity as useEntity7 } from "@oppy/omniv2-entity";
import { useParams } from "react-router-dom";
function useRcsUpdatePage() {
  const { entityId } = useEntity7();
  const { rcsSettingsId = "" } = useParams();
  const onErrorCallback = () => {
  };
  const { data } = useSuspenseGetRcsSettings({
    entityId,
    rcsSettingsId,
    onErrorCallback
  });
  const { updateRcsSettings, isPending } = useUpdateRcsSettings();
  const handleSubmit = (data2) => updateRcsSettings(data2);
  return {
    data,
    isPending,
    handleSubmit
  };
}

// src/pages/rcs/update/rcs-update.page.tsx
import { jsx as jsx13 } from "react/jsx-runtime";
var RcsWizardForm2 = lazy4(
  () => import("./rcs-wizard-form.FEOVWAO4.js")
);
var WizardForm = function() {
  const { data, isPending, handleSubmit } = useRcsUpdatePage();
  return /* @__PURE__ */ jsx13(
    RcsWizardForm2,
    {
      title: "Modification de RCS",
      handleSubmit,
      initialValues: data,
      isSubmitting: isPending,
      initialStep: 1
    }
  );
};
function RcsUpdatePage() {
  return /* @__PURE__ */ jsx13(Suspense5, { fallback: /* @__PURE__ */ jsx13(Loading5, { centered: true }), children: /* @__PURE__ */ jsx13(WizardForm, {}) });
}

// src/pages/stats/dashboard/rcs-stats-dashboard.page.tsx
import { Button, Card as Card2, Loading as Loading6, Tab } from "@oppy/omniv2-ui";
import { lazy as lazy5, Suspense as Suspense6 } from "react";
import { jsx as jsx14, jsxs as jsxs8 } from "react/jsx-runtime";
var GlobalStatistics = lazy5(
  () => import("./global-statistics.3K7AWZNX.js")
);
var CampaignSettingStatistics = lazy5(
  () => import("./campaign-setting-statistics.DLYM5BEC.js")
);
function RcsStatsDashboardPage() {
  return /* @__PURE__ */ jsx14(Card2, { children: /* @__PURE__ */ jsxs8("div", { className: "grid h-full grid-cols-3 gap-4", children: [
    /* @__PURE__ */ jsxs8("div", { className: "col-span-2 flex flex-col", children: [
      /* @__PURE__ */ jsx14(Suspense6, { fallback: /* @__PURE__ */ jsx14(Loading6, { centered: true }), children: /* @__PURE__ */ jsx14(CampaignSettingStatistics, {}) }),
      /* @__PURE__ */ jsx14(Suspense6, { fallback: /* @__PURE__ */ jsx14(Loading6, { centered: true }), children: /* @__PURE__ */ jsx14(GlobalStatistics, {}) })
    ] }),
    /* @__PURE__ */ jsxs8(Card2, { className: "col-span-1", children: [
      /* @__PURE__ */ jsx14("div", { className: "mb-8 text-lg", children: "Contenu de la campagne" }),
      /* @__PURE__ */ jsx14(
        Tab,
        {
          size: "full",
          items: [
            {
              header: /* @__PURE__ */ jsx14("div", { children: "RCS" }),
              children: /* @__PURE__ */ jsx14("div", { className: "flex h-screen max-h-[575px] justify-center", children: /* @__PURE__ */ jsx14(MessageVisualizer, { isInteractive: false, children: /* @__PURE__ */ jsx14("div", { className: "mt-2 flex h-full flex-col justify-between", children: /* @__PURE__ */ jsx14(Button, { disabled: true, children: "Voir la conversation" }) }) }) })
            },
            {
              header: /* @__PURE__ */ jsx14("div", { children: "SMS" }),
              children: /* @__PURE__ */ jsx14(BaseVisualizer, { children: "Iphone" }),
              disabled: true
            }
          ]
        }
      )
    ] })
  ] }) });
}

// src/pages/stats/deliverability/rcs-stats-deliverability.page.tsx
import { Card as Card3, Loading as Loading7 } from "@oppy/omniv2-ui";
import { lazy as lazy6, Suspense as Suspense7 } from "react";
import { jsx as jsx15, jsxs as jsxs9 } from "react/jsx-runtime";
var ChartCard = lazy6(() => import("./chart-card.464SW3WH.js"));
var StatusDetailsDeliverabilityTable = lazy6(
  () => import("./status-details-deliverability-table.5BKC4P7X.js")
);
function RcsStatsDeliverabilityPage() {
  return /* @__PURE__ */ jsx15(
    Suspense7,
    {
      fallback: /* @__PURE__ */ jsx15("div", { className: "h-full rounded border-b border-b-gray-400 bg-white p-4 shadow", children: /* @__PURE__ */ jsx15(Loading7, { centered: true }) }),
      children: /* @__PURE__ */ jsxs9(Card3, { children: [
        /* @__PURE__ */ jsx15(ChartCard, {}),
        /* @__PURE__ */ jsx15(
          StatusDetailsDeliverabilityTable,
          {
            header: "Details des statuts",
            inputLabel: "Rechercher un num\xE9ro"
          }
        )
      ] })
    }
  );
}

// src/pages/stats/engagement/rcs-stats-engagement.page.tsx
import { Card as Card5, Tab as Tab2 } from "@oppy/omniv2-ui";

// src/pages/stats/engagement/components/card-icon.tsx
import { Card as Card4, cn as cn2, Icon as Icon3 } from "@oppy/omniv2-ui";
import { jsx as jsx16, jsxs as jsxs10 } from "react/jsx-runtime";
function CardIcon({
  icon,
  value,
  label,
  color
}) {
  return /* @__PURE__ */ jsx16(Card4, { className: "bg-body-background shadow-none", children: /* @__PURE__ */ jsxs10("div", { className: "flex gap-2", children: [
    /* @__PURE__ */ jsx16(
      "div",
      {
        className: cn2(
          "size-10 bg-primary flex items-center justify-center rounded-lg shadow",
          color
        ),
        children: /* @__PURE__ */ jsx16(Icon3, { icon, className: "text-white" })
      }
    ),
    /* @__PURE__ */ jsxs10("div", { children: [
      /* @__PURE__ */ jsx16("div", { className: "text-xs text-opacity-70", children: label }),
      /* @__PURE__ */ jsx16("div", { className: "text-xl", children: value })
    ] })
  ] }) });
}

// src/pages/stats/engagement/components/line-graph-engagement.tsx
import { Chart, Heading1 as Heading14 } from "@oppy/omniv2-ui";
import { useMemo } from "react";
import { useRef } from "react";
import { jsx as jsx17, jsxs as jsxs11 } from "react/jsx-runtime";
function LineGraphEngagement({
  title,
  labels,
  lineValues,
  color
}) {
  const hasAnimatedRef = useRef(false);
  const chartProps = useMemo(
    () => ({
      type: "line",
      data: {
        labels,
        datasets: [
          {
            data: lineValues,
            borderColor: color,
            backgroundColor: (context) => {
              const chart = context.chart;
              const { ctx, chartArea } = chart;
              if (!chartArea) {
                return null;
              }
              const gradient = ctx.createLinearGradient(
                0,
                chartArea.top,
                0,
                chartArea.bottom
              );
              gradient.addColorStop(0, "rgba(242, 235, 255, 1)");
              gradient.addColorStop(1, "rgba(242, 235, 255, 0)");
              return gradient;
            },
            fill: true,
            tension: 0.4
          }
        ]
      },
      options: {
        responsive: true,
        plugins: {
          legend: {
            display: false
          }
        },
        animation: hasAnimatedRef.current ? false : {
          duration: 1e3,
          onComplete: () => {
            hasAnimatedRef.current = true;
          }
        },
        scales: {
          x: {
            grid: {
              display: false
            }
          },
          y: {
            beginAtZero: true
          }
        }
      }
    }),
    [labels, lineValues, color]
  );
  return /* @__PURE__ */ jsxs11("div", { children: [
    title && /* @__PURE__ */ jsx17(Heading14, { className: "mb-4 text-center", children: title }),
    /* @__PURE__ */ jsx17(Chart, { ...chartProps, className: "my-4" })
  ] });
}

// src/pages/stats/engagement/components/rcs-workflow-read-only/rcs-workflow-read-only.tsx
import { Background, BackgroundVariant, ReactFlow } from "@xyflow/react";
import { useState as useState2 } from "react";

// src/pages/stats/engagement/hooks/use-rcs-workflow-read-only.ts
import { useEntity as useEntity8 } from "@oppy/omniv2-entity";

// src/pages/stats/engagement/components/rcs-workflow-read-only/rcs-workflow-read-only.tsx
import { jsx as jsx18 } from "react/jsx-runtime";

// src/pages/stats/engagement/components/table-engagement.tsx
import {
  faMagnifyingGlass,
  Heading1 as Heading15,
  InputIcon,
  SelectMultiple,
  Table as Table4
} from "@oppy/omniv2-ui";
import { useDebounceCallback } from "@oppy/omniv2-utils";
import { useCallback, useState as useState3 } from "react";
import { Fragment as Fragment4, jsx as jsx19, jsxs as jsxs12 } from "react/jsx-runtime";
var PHONE_NUMBER_FILTER = "searchFilter.PhoneNumber";
var CTA_FILTER = "searchFilter.callToActions";
function TableEngagement({
  first,
  isLoading,
  rows,
  sortField,
  sortOrder,
  totalItems,
  items,
  onPage,
  onSort,
  onFilter,
  filterItems,
  getFilter,
  getAllFilter
}) {
  const [searchContactNumber, setSearchContactNumber] = useState3(
    getFilter(PHONE_NUMBER_FILTER) ?? ""
  );
  const [filter, setFilter] = useState3(
    getAllFilter(CTA_FILTER)
  );
  const debouncedInputChange = useDebounceCallback((value) => {
    onFilter(PHONE_NUMBER_FILTER, value);
  }, 500);
  const handleInputChange = useCallback(
    (event) => {
      setSearchContactNumber(event.target.value);
      debouncedInputChange(event.target.value);
    },
    [debouncedInputChange]
  );
  const debouncedSelectMultipleChange = useDebounceCallback(
    (value) => {
      onFilter(CTA_FILTER, value);
    },
    500
  );
  const handleSelectMultipleChange = useCallback(
    (event) => {
      setFilter(event);
      debouncedSelectMultipleChange(event);
    },
    [debouncedSelectMultipleChange]
  );
  return /* @__PURE__ */ jsxs12(Fragment4, { children: [
    /* @__PURE__ */ jsx19("div", { className: "mb-5", children: /* @__PURE__ */ jsx19(Heading15, { children: "D\xE9tails des statuts" }) }),
    /* @__PURE__ */ jsx19(
      Table4,
      {
        columns: COLUMNS,
        first,
        loading: isLoading,
        rows,
        sortField,
        sortOrder,
        showTotalRecords: true,
        totalRecords: totalItems,
        value: items ?? [],
        onPage,
        onSort,
        header: /* @__PURE__ */ jsxs12("div", { className: "flex items-center justify-between", children: [
          /* @__PURE__ */ jsx19(
            InputIcon,
            {
              className: "border-none",
              placeholder: "Rechercher un num\xE9ro",
              keyfilter: "num",
              maxLength: 10,
              icon: faMagnifyingGlass,
              onChange: (e) => handleInputChange(e),
              value: searchContactNumber
            }
          ),
          /* @__PURE__ */ jsx19(
            SelectMultiple,
            {
              value: filter,
              onChange: handleSelectMultipleChange,
              options: filterItems,
              optionLabel: "value",
              placeholder: "Filtre"
            }
          )
        ] })
      }
    )
  ] });
}

// src/pages/stats/engagement/hooks/use-stats-engagement-page.ts
import { useEntity as useEntity9 } from "@oppy/omniv2-entity";
import { useTablePagination as useTablePagination4 } from "@oppy/omniv2-ui";
import { useParams as useParams2 } from "react-router-dom";
function useStatsEngagementPage() {
  const { entityId } = useEntity9();
  const { campaignId } = useParams2();
  const { searchParams, ...tablePaginationProps } = useTablePagination4();
  const { isLoading, data = {} } = useGetStatsEngagement({
    entityId,
    campaignId
  });
  const {
    isLoading: isLoadingTable,
    data: dataTable = {}
  } = useGetStatsTableEngagement({
    entityId,
    campaignId,
    searchParams
  });
  const { isLoading: isLoadingCTA, data: ctaList = [] } = useGetCTAEngagement({
    entityId,
    campaignId
  });
  return {
    isLoading: isLoading || isLoadingTable || isLoadingCTA,
    data,
    dataTable,
    ctaList,
    searchParams,
    ...tablePaginationProps
  };
}

// src/pages/stats/engagement/rcs-stats-engagement.page.tsx
import { Fragment as Fragment5, jsx as jsx20, jsxs as jsxs13 } from "react/jsx-runtime";
function RcsStatsEngagementPage() {
  const {
    data,
    ctaList,
    dataTable,
    isLoading,
    params: { first, rows, sortField, sortOrder, getFilter, getAllFilter },
    onPage,
    onSort,
    onFilter
  } = useStatsEngagementPage();
  return /* @__PURE__ */ jsx20(Card5, { children: /* @__PURE__ */ jsx20(
    Tab2,
    {
      size: "full",
      items: [
        {
          header: /* @__PURE__ */ jsx20("div", { children: "RCS" }),
          children: /* @__PURE__ */ jsxs13(Fragment5, { children: [
            /* @__PURE__ */ jsx20("div", { className: "grid grid-cols-4 gap-4", children: data?.engagementHeaderStats?.map(
              (cardHeaderEngagementStat, index) => /* @__PURE__ */ jsx20(
                CardIcon,
                {
                  icon: cardHeaderEngagementStat.icon,
                  label: cardHeaderEngagementStat.label,
                  value: cardHeaderEngagementStat.value,
                  color: cardHeaderEngagementStat.color
                },
                `${index}`
              )
            ) }),
            /* @__PURE__ */ jsx20("div", { className: "grid grid-cols-2 gap-4", children: data?.engagementStats?.map((graphEngagementStat, index) => /* @__PURE__ */ jsx20(
              LineGraphEngagement,
              {
                title: graphEngagementStat.title,
                lineValues: graphEngagementStat.lineValues,
                labels: graphEngagementStat.labels,
                color: graphEngagementStat.color ?? void 0
              },
              index
            )) }),
            /* @__PURE__ */ jsx20("div", { className: "mt-4 grid h-full gap-4", children: /* @__PURE__ */ jsx20(Card5, { children: /* @__PURE__ */ jsx20(
              TableEngagement,
              {
                first,
                isLoading,
                rows,
                sortField,
                sortOrder,
                totalItems: dataTable?.totalItems,
                items: dataTable?.items ?? [],
                onPage,
                onSort,
                onFilter,
                filterItems: ctaList,
                getFilter,
                getAllFilter
              }
            ) }) })
          ] })
        },
        {
          header: /* @__PURE__ */ jsx20("div", { children: "SMS" }),
          children: /* @__PURE__ */ jsx20(Fragment5, {}),
          disabled: true
        }
      ]
    }
  ) });
}

// src/pages/stats/unsubscription/rcs-stats-unsubscription.page.tsx
import {
  Badge as Badge3,
  Button as Button2,
  Card as Card6,
  faDownload,
  faMagnifyingGlass as faMagnifyingGlass2,
  Heading1 as Heading16,
  Icon as Icon4,
  InputIcon as InputIcon2,
  Table as Table5
} from "@oppy/omniv2-ui";
import { useDebounceCallback as useDebounceCallback2 } from "@oppy/omniv2-utils";
import { useState as useState4 } from "react";

// src/pages/stats/unsubscription/hooks/use-stats-unsubscription-page.ts
import { useEntity as useEntity10 } from "@oppy/omniv2-entity";
import { useTablePagination as useTablePagination5 } from "@oppy/omniv2-ui";
import { useParams as useParams3 } from "react-router-dom";
function useStatsUnsubscriptionPage() {
  const { entityId } = useEntity10();
  const { campaignId = "" } = useParams3();
  const { searchParams, ...tablePaginationProps } = useTablePagination5();
  const { isLoading, data = {} } = useGetStatsUnsubscription({
    entityId,
    searchParams,
    campaignId
  });
  return {
    isLoading,
    data,
    searchParams,
    ...tablePaginationProps
  };
}

// src/pages/stats/unsubscription/rcs-stats-unsubscription.constants.tsx
import { formatDate as formatDate3, formatPhone } from "@oppy/omniv2-utils";
var COLUMNS4 = [
  {
    field: "phoneNumber",
    header: "Numero",
    body: ({ phoneNumber }) => formatPhone(phoneNumber)
  },
  {
    field: "firstContactAt",
    header: "Date",
    body: ({ firstContactAt }) => formatDate3(firstContactAt)
  },
  {
    field: "optOutAt",
    header: "Date d'envoie",
    body: ({ optOutAt }) => formatDate3(optOutAt)
  },
  {
    field: "canal",
    header: "Canal"
  }
];

// src/pages/stats/unsubscription/rcs-stats-unsubscription.page.tsx
import { jsx as jsx21, jsxs as jsxs14 } from "react/jsx-runtime";
var PHONE_NUMBER_FILTER2 = "phoneNumber";
function RcsStatsUnsubscriptionPage() {
  const {
    data: { items = [], totalItems },
    isLoading,
    params: { first, rows, sortField, sortOrder, getFilter },
    onPage,
    onSort,
    onFilter
  } = useStatsUnsubscriptionPage();
  const [phoneNumberSearch, setPhoneNumberSearch] = useState4(
    getFilter(PHONE_NUMBER_FILTER2) ?? ""
  );
  const debouncedInputChange = useDebounceCallback2((value) => {
    onFilter(PHONE_NUMBER_FILTER2, value);
  }, 500);
  const handleInputChange = (event) => {
    const value = event.target.value ?? "";
    setPhoneNumberSearch(value);
    debouncedInputChange(value);
  };
  return /* @__PURE__ */ jsxs14(Card6, { children: [
    /* @__PURE__ */ jsxs14("div", { className: "mb-8 flex items-center gap-4 text-lg", children: [
      /* @__PURE__ */ jsx21(Heading16, { children: "D\xE9tails des stops" }),
      !!totalItems && /* @__PURE__ */ jsx21(Badge3, { variant: "info", children: totalItems })
    ] }),
    /* @__PURE__ */ jsx21(
      Table5,
      {
        columns: COLUMNS4,
        first,
        loading: isLoading,
        rows,
        sortField,
        sortOrder,
        showTotalRecords: true,
        totalRecords: totalItems,
        value: items,
        onPage,
        onSort,
        header: /* @__PURE__ */ jsxs14("div", { className: "mb-5 flex items-center justify-between", children: [
          /* @__PURE__ */ jsx21(
            InputIcon2,
            {
              className: "border-none pr-8",
              placeholder: "Rechercher un num\xE9ro",
              keyfilter: "num",
              maxLength: 10,
              icon: faMagnifyingGlass2,
              onChange: (e) => handleInputChange(e),
              value: phoneNumberSearch
            }
          ),
          /* @__PURE__ */ jsxs14(Button2, { variant: "outlined-info", disabled: true, children: [
            /* @__PURE__ */ jsx21(Icon4, { icon: faDownload, className: "mr-2" }),
            "T\xE9l\xE9charger"
          ] })
        ] })
      }
    )
  ] });
}

// src/lib/core/routes.tsx
import { jsx as jsx22 } from "react/jsx-runtime";
var RCS_SETTINGS_ID = ":rcsSettingsId";
var RCS_AGENT_ID = ":rcsAgentId";
var CAMPAIGN_SETTINGS_ID = ":campaignId";
var routes = [
  {
    id: "rcs-root",
    path: "rcs",
    element: /* @__PURE__ */ jsx22(
      RouteGuard,
      {
        resource: RESOURCES5.RCS_SETTING,
        permission: PERMISSIONS3.ACCESS,
        children: /* @__PURE__ */ jsx22(Outlet, {})
      }
    ),
    children: [
      {
        id: "rcs-list",
        index: true,
        element: /* @__PURE__ */ jsx22(
          RouteGuard,
          {
            resource: RESOURCES5.RCS_SETTING,
            permission: PERMISSIONS3.READ,
            children: /* @__PURE__ */ jsx22(RcsListPage, {})
          }
        )
      },
      {
        path: "create",
        element: /* @__PURE__ */ jsx22(
          RouteGuard,
          {
            resource: RESOURCES5.RCS_SETTING,
            permission: PERMISSIONS3.CREATE,
            children: /* @__PURE__ */ jsx22(RcsCreatePage, {})
          }
        )
      },
      {
        path: RCS_SETTINGS_ID,
        element: /* @__PURE__ */ jsx22(
          RouteGuard,
          {
            resource: RESOURCES5.RCS_SETTING,
            permission: PERMISSIONS3.READ,
            children: /* @__PURE__ */ jsx22(RcsUpdatePage, {})
          }
        )
      },
      {
        path: "agent",
        element: /* @__PURE__ */ jsx22(
          RouteGuard,
          {
            resource: RESOURCES5.RCS_AGENT,
            permission: PERMISSIONS3.ACCESS,
            children: /* @__PURE__ */ jsx22(Outlet, {})
          }
        ),
        children: [
          {
            id: "rcs-list-agent",
            index: true,
            element: /* @__PURE__ */ jsx22(
              RouteGuard,
              {
                resource: RESOURCES5.RCS_AGENT,
                permission: PERMISSIONS3.READ,
                children: /* @__PURE__ */ jsx22(RcsAgentListPage, {})
              }
            )
          },
          {
            id: "rcs-agent-create",
            path: "create",
            element: /* @__PURE__ */ jsx22(
              RouteGuard,
              {
                resource: RESOURCES5.RCS_AGENT,
                permission: PERMISSIONS3.CREATE,
                children: /* @__PURE__ */ jsx22(RcsAgentCreatePage, {})
              }
            )
          },
          {
            path: RCS_AGENT_ID,
            element: /* @__PURE__ */ jsx22(
              RouteGuard,
              {
                resource: RESOURCES5.RCS_AGENT,
                permission: PERMISSIONS3.READ,
                children: /* @__PURE__ */ jsx22(RcsAgentConsultPage, {})
              }
            )
          }
        ]
      },
      {
        id: "rcs-campaigns",
        path: "campaigns",
        element: /* @__PURE__ */ jsx22(
          RouteGuard,
          {
            resource: RESOURCES5.RCS_CAMPAIGN,
            permission: PERMISSIONS3.ACCESS,
            children: /* @__PURE__ */ jsx22(Outlet, {})
          }
        ),
        children: [
          {
            index: true,
            element: /* @__PURE__ */ jsx22(
              RouteGuard,
              {
                resource: RESOURCES5.RCS_CAMPAIGN,
                permission: PERMISSIONS3.READ,
                children: /* @__PURE__ */ jsx22(RcsCampaignsPage, {})
              }
            )
          },
          {
            path: CAMPAIGN_SETTINGS_ID,
            element: /* @__PURE__ */ jsx22(
              RouteGuard,
              {
                resource: RESOURCES5.RCS_CAMPAIGN,
                permission: PERMISSIONS3.READ,
                children: /* @__PURE__ */ jsx22(RCSDashboardStatisticsMenu, {})
              }
            ),
            children: [
              {
                path: "dashboard",
                element: /* @__PURE__ */ jsx22(
                  RouteGuard,
                  {
                    resource: RESOURCES5.RCS_CAMPAIGN,
                    permission: PERMISSIONS3.READ,
                    children: /* @__PURE__ */ jsx22(RcsStatsDashboardPage, {})
                  }
                )
              },
              {
                path: "deliverability",
                element: /* @__PURE__ */ jsx22(
                  RouteGuard,
                  {
                    resource: RESOURCES5.RCS_CAMPAIGN,
                    permission: PERMISSIONS3.READ,
                    children: /* @__PURE__ */ jsx22(RcsStatsDeliverabilityPage, {})
                  }
                )
              },
              {
                path: "engagement",
                element: /* @__PURE__ */ jsx22(
                  RouteGuard,
                  {
                    resource: RESOURCES5.RCS_CAMPAIGN,
                    permission: PERMISSIONS3.READ,
                    children: /* @__PURE__ */ jsx22(RcsStatsEngagementPage, {})
                  }
                )
              },
              {
                path: "unsubscription",
                element: /* @__PURE__ */ jsx22(
                  RouteGuard,
                  {
                    resource: RESOURCES5.RCS_CAMPAIGN,
                    permission: PERMISSIONS3.READ,
                    children: /* @__PURE__ */ jsx22(RcsStatsUnsubscriptionPage, {})
                  }
                )
              }
            ]
          }
        ]
      }
    ]
  }
];
var routes_default = routes;
export {
  PREDEFINED_REGEX,
  getPredefinedRegexById,
  isPredefinedRegexType,
  navigations_default as navigations,
  routes_default as routes
};
